export type StopWasmExports = {
  getBootstrapKeyBase64: () => string;
  initializeSessionFromPlaintext: (plaintextBase64: string) => string;
  submitAnswer: (questionId: string, text: string) => string;
  getPlayState: () => string;
  buildFinishPayload: () => string;
  disposeSession: () => void;
};

type Fn = (...args: unknown[]) => unknown;

function pickMethod(obj: Record<string, unknown>, ...names: string[]): Fn | undefined {
  for (const name of names) {
    const fn = obj[name];
    if (typeof fn === 'function') return fn as Fn;
  }
  return undefined;
}

function wrapRawExports(raw: Record<string, unknown>): StopWasmExports | null {
  const getBootstrapKeyBase64 = pickMethod(raw, 'GetBootstrapKeyBase64', 'getBootstrapKeyBase64');
  const initializeSessionFromPlaintext = pickMethod(
    raw,
    'InitializeSessionFromPlaintext',
    'initializeSessionFromPlaintext'
  );
  const submitAnswer = pickMethod(raw, 'SubmitAnswer', 'submitAnswer');
  const getPlayState = pickMethod(raw, 'GetPlayState', 'getPlayState');
  const buildFinishPayload = pickMethod(raw, 'BuildFinishPayload', 'buildFinishPayload');
  const disposeSession = pickMethod(raw, 'DisposeSession', 'disposeSession');

  if (
    !getBootstrapKeyBase64 ||
    !initializeSessionFromPlaintext ||
    !submitAnswer ||
    !getPlayState ||
    !buildFinishPayload ||
    !disposeSession
  ) {
    return null;
  }

  return {
    getBootstrapKeyBase64: () => getBootstrapKeyBase64() as string,
    initializeSessionFromPlaintext: (plaintextBase64: string) =>
      initializeSessionFromPlaintext(plaintextBase64) as string,
    submitAnswer: (questionId: string, text: string) =>
      submitAnswer(questionId, text) as string,
    getPlayState: () => getPlayState() as string,
    buildFinishPayload: () => buildFinishPayload() as string,
    disposeSession: () => {
      disposeSession();
    },
  };
}

function deepFindWasmExports(node: unknown, depth = 0): StopWasmExports | null {
  if (!node || typeof node !== 'object' || depth > 14) return null;

  const wrapped = wrapRawExports(node as Record<string, unknown>);
  if (wrapped) return wrapped;

  for (const value of Object.values(node as Record<string, unknown>)) {
    if (typeof value === 'function') continue;
    const found = deepFindWasmExports(value, depth + 1);
    if (found) return found;
  }

  return null;
}

const EXPORT_PATHS: string[][] = [
  ['Quizter', 'Stop', 'Wasm', 'StopWasmExports'],
  ['Stop', 'Wasm', 'StopWasmExports'],
  ['StopWasmExports'],
];

function resolvePath(root: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = root;
  for (const segment of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[segment];
  }
  return cur;
}

export function resolveStopWasmExports(
  assemblyRoot: Record<string, unknown>
): StopWasmExports {
  for (const path of EXPORT_PATHS) {
    const candidate = resolvePath(assemblyRoot, path);
    const wrapped =
      candidate && typeof candidate === 'object'
        ? wrapRawExports(candidate as Record<string, unknown>)
        : null;
    if (wrapped) return wrapped;
  }

  const deep = deepFindWasmExports(assemblyRoot);
  if (deep) return deep;

  const topKeys = Object.keys(assemblyRoot).join(', ') || '(empty)';
  throw new Error(`Stop WASM: engine exports not found. Assembly keys: ${topKeys}`);
}

export async function loadStopAssemblyExports(
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>,
  mainAssemblyName: string
): Promise<StopWasmExports> {
  const names = [
    mainAssemblyName,
    mainAssemblyName.replace(/\.dll$/i, '.wasm'),
    'Quizter.Stop.Wasm.wasm',
    'Quizter.Stop.Wasm.dll',
    'Quizter.Stop.Wasm',
  ];
  const tried = new Set<string>();

  for (const name of names) {
    if (!name || tried.has(name)) continue;
    tried.add(name);
    try {
      const assembly = await getAssemblyExports(name);
      if (assembly && typeof assembly === 'object') {
        return resolveStopWasmExports(assembly);
      }
    } catch {
      // try next assembly name
    }
  }

  throw new Error('Stop WASM: could not load assembly exports. Hard-refresh the browser.');
}
